<svelte:options runes={false} />

<script>
  import { minorToMoney } from '../../api/invoices';
  import {
    paymentCopy,
    paymentDisplayStatus,
    paymentHeading,
    payerStatusLabel,
    statusTone,
  } from '../status-labels.mjs';
  import { formatHours, withCurrency } from '../format.mjs';
  import ReceiptItemRow from '../components/ReceiptItemRow.svelte';
  import RouteState from '../components/RouteState.svelte';
  import SectionHeading from '../components/SectionHeading.svelte';
  import StatRow from '../components/StatRow.svelte';
  import StatusPill from '../components/StatusPill.svelte';

  export let paymentAttempt;
  export let attemptItems;
  export let operatorAccount;
  export let checkoutReturnHint;
  export let startingNextStep;
  export let retryingCheckout;
  export let onBeginNextStep;
  export let onRetryCheckout;

  $: payTotal = paymentAttempt
    ? withCurrency(
        minorToMoney(paymentAttempt.totalMinor, paymentAttempt.currency),
        paymentAttempt.currency,
      )
    : '';
  // payerNotice is the bearer-safe word derived from providerReviewCategory
  // (decided 2026-08-09 during the S02 read, re-audit F2). A refund or dispute
  // usually arrives on an attempt that is already `paid`, so the raw status
  // would keep saying the payment went through; the notice takes precedence
  // over the status for everything this page *says*, while the action buttons
  // below stay keyed on the real status.
  $: displayStatus = paymentDisplayStatus(
    paymentAttempt?.status,
    paymentAttempt?.payerNotice,
  );
  // '' for a state withheld from the payer. The row is omitted rather than
  // filled with a stand-in. (No state is withheld today.)
  $: payerStatus = payerStatusLabel(displayStatus);
</script>

<!-- An unresolvable token is this route's not-found state and never reaches
     here; App renders it (imports/ui/route-states.mjs). -->
<section class="panel intent-panel payer-shell">
  <SectionHeading
    eyebrow={paymentAttempt.invoiceReference || 'Your payment'}
    title={paymentHeading(displayStatus, checkoutReturnHint)}
  >
    {#if paymentAttempt.invoiceLabel}
      <p class="quiet">
        {paymentAttempt.invoiceLabel} · reported by
        {operatorAccount?.name || 'the person who did the work'}
      </p>
    {/if}
    <svelte:fragment slot="trailing">
      <strong>{payTotal}</strong>
    </svelte:fragment>
  </SectionHeading>

  <p class="payment-copy">
    {paymentCopy(displayStatus, checkoutReturnHint)}
  </p>

  {#if paymentAttempt.status === 'pending_next_step'}
    <button class="primary" type="button" disabled={startingNextStep} on:click={onBeginNextStep}>
      Pay {payTotal}
    </button>
  {:else if paymentAttempt.status === 'checkout_created' && checkoutReturnHint === 'cancel'}
    <button class="primary" type="button" disabled={retryingCheckout} on:click={onRetryCheckout}>
      Try again
    </button>
  {:else if paymentAttempt.status === 'checkout_created'}
    <button class="primary" type="button" disabled={startingNextStep} on:click={onBeginNextStep}>
      Continue to payment
    </button>
  {:else if paymentAttempt.status === 'expired'}
    <button class="primary" type="button" disabled={retryingCheckout} on:click={onRetryCheckout}>
      Try again
    </button>
  {/if}

  <div class="stats">
    {#if payerStatus}
      <div>
        <span>Status</span>
        <strong><StatusPill label={payerStatus} tone={statusTone(displayStatus)} /></strong>
      </div>
    {/if}
    <StatRow label="Hours" value={formatHours(paymentAttempt.hours)} />
    <StatRow label="Your rate" value="{minorToMoney(paymentAttempt.hourlyRateMinor, paymentAttempt.currency)}/h" />
    <StatRow label="Costs" value={minorToMoney(paymentAttempt.fixedCostsMinor || 0, paymentAttempt.currency)} />
    <StatRow label="You added" value={minorToMoney(paymentAttempt.flatAdditionMinor || 0, paymentAttempt.currency)} />
    <StatRow label="Tax" value={minorToMoney(paymentAttempt.taxMinor, paymentAttempt.currency)} />
  </div>

  {#if attemptItems.length > 0}
    <section class="bit-list" aria-label="What you're paying for">
      {#each attemptItems as item (item._id)}
        <ReceiptItemRow {item} currency={paymentAttempt.currency} />
      {/each}
    </section>
  {:else}
    <!-- Empty renders in place: the amount and its status above are still the
         things this page exists to state. -->
    <RouteState routeKey="pay" state="empty" bare />
  {/if}
</section>
